<script setup>
import { ref, computed } from "vue";
import _find from "lodash/find";
import _findIndex from "lodash/findIndex";
import _isArray from "lodash/isArray";
import _cloneDeep from "lodash/cloneDeep";
import { CheckIcon } from "@heroicons/vue/20/solid";
import { nextTick } from "vue";

const props = defineProps(["modelValue", "steps", "initial"]);
defineEmits(["update:modelValue"]);

const changeStepMode = ref("next");
const stepperElement = ref();

const getCurrentStep = (steps) => {
    let step = _find(steps, { status: "current" });

    if (step && step.children && step.children.length > 0) {
        return getCurrentStep(step.children);
    }

    return step;
};

const setCurrentStep = (key, _steps) => {
    key = _cloneDeep(key);

    if (_steps === undefined) {
        _steps = props.steps;
    }
    if (_isArray(key) && key.length > 0) {
        const index = _findIndex(_steps, ["key", key[0]]);
        setCurrentStep(key[0], _steps);
        if (key.length > 1 && _steps[index].children && _steps[index].children.length > 0) {
            const firstKey = key.shift();
            key[0] = firstKey + "-" + key[0];
            setCurrentStep(key, _steps[index].children);
        }
    } else {
        const index = _findIndex(_steps, ["key", key]);
        for (let i = 0; i < _steps.length; i++) {
            if (i < index) {
                _steps[i].status = "complete";
                if (_steps[i].children && _steps[i].children.length > 0) {
                    for (let j = 0; j < _steps[i].children.length; j++) {
                        _steps[i].children[j].status = "complete";
                    }
                }
            } else if (i > index) {
                _steps[i].status = "upcoming";
                if (_steps[i].children && _steps[i].children.length > 0) {
                    for (let j = 0; j < _steps[i].children.length; j++) {
                        _steps[i].children[j].status = "upcoming";
                    }
                }
            } else {
                _steps[i].status = "current";
            }
        }
    }
};

const changeStep = async (event, mode) => {
    if (event === undefined) return;

    // call after handler
    if (mode === "next" && currentStep.value?.after !== undefined) {
        if (!(await currentStep.value?.after())) return;
    }

    changeStepMode.value = mode;

    // call handler
    if (typeof event.handler === "function") {
        event.handler();
    } else {
        setCurrentStep(event.handler);
    }

    nextTick(() => {
        stepperElement.value.scrollIntoView({ behavior: "smooth" });
    });

    // call before handler
    if (mode === "next" && currentStep.value?.before !== undefined) {
        if (!(await currentStep.value?.before())) return;
    }
};

const currentStep = computed(() => {
    return getCurrentStep(props.steps);
});

const back = computed(() => {
    // has back
    if (currentStep.value.back === undefined) return;

    return () => changeStep(currentStep.value.back, "back");
});

const next = computed(() => {
    // has next
    if (currentStep.value.next === undefined) return;

    return () => changeStep(currentStep.value.next, "next");
});

const skip = computed(() => {
    // has skip
    if (currentStep.value.skip === undefined) return;

    return () => changeStep(currentStep.value.skip, "next");
});

if (props.initial !== undefined) {
    setCurrentStep(props.initial);
}

defineExpose({ setCurrentStep });
</script>

<template>
    <div class="flex flex-col" ref="stepperElement">
        <slot name="before" />
        <transition
            enter-active-class="transition duration-100 ease-out"
            :enter-from-class="'transform opacity-0 '.concat(changeStepMode === 'next' ? 'translate-x-5' : '-translate-x-5')"
            enter-to-class="transform opacity-100"
            leave-active-class="transition duration-75 ease-in "
            leave-from-class="transform opacity-100"
            :leave-to-class="'transform opacity-0 '.concat(changeStepMode === 'next' ? '-translate-x-5' : 'translate-x-5')"
            mode="out-in"
        >
            <Component
                :key="currentStep.key"
                :is="currentStep.component"
                :modelValue="modelValue"
                @update:modelValue="$emit('update:modelValue', $event)"
                v-bind="currentStep.params"
                :step="currentStep"
                @back="back"
                @next="next"
                @skip="skip"
            />
        </transition>
        <slot name="after" />
    </div>
</template>
